import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Edit2,
  Trash2,
  Save,
  X,
  Users,
  Search,
  UserPlus,
  Calendar,
  Award,
  ChevronLeft,
  ExternalLink,
  CheckCircle,
  Trophy
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import BeltBadge, { BeltIndicator, getBeltName } from '../shared/BeltBadge';
import { useBelts } from '../../contexts/BeltContext';
import adminApi from '../../services/adminApi';

import BeltPromotionEditor from './BeltPromotionEditor';
import AttendanceManager from './AttendanceManager';
import TournamentEditor from './TournamentEditor';
import { PageTitle } from './shared';
import { SECTION_GROUPS } from './shared/PortalSectionCheckboxGrid';
import {
  getRememberedPageValue,
  rememberPageValue,
} from './navigation/adminPlaceMemory';
import { getAdminUser } from './navigation/sectionAccess';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Legacy getAuthHeaders for components that still need it
const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('googleAuthToken')}`,
  'Content-Type': 'application/json'
});

// ============================================
// Student Editor Modal/Form
// ============================================
const StudentEditor = ({ student, customers, students, programs, onSave, onCancel }) => {
  const { getBeltOptions } = useBelts();
  const beltOptions = getBeltOptions();

  const [formData, setFormData] = useState({
    customer_id: student?.customer_id || '',
    name: student?.name || '',
    email: student?.email || '',
    date_of_birth: student?.date_of_birth || '',
    current_belt: student?.current_belt || 'white',
    current_stripes: student?.current_stripes || 0,
    program_ids: student?.program_ids || [],
    start_date: student?.start_date || new Date().toISOString().split('T')[0],
    notes: student?.notes || '',
    portal_section_overrides: student?.portal_section_overrides || null,
  });
  const [effectiveSections, setEffectiveSections] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch effective sections for existing students
  useEffect(() => {
    if (!student?.id) return;
    const fetchEffective = async () => {
      try {
        const res = await adminApi.get(`/api/admin/students/${student.id}/effective-sections`);
        if (res.ok) setEffectiveSections(await res.json());
      } catch { /* ignore - effective sections is optional */ }
    };
    fetchEffective();
  }, [student?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || !formData.name) {
      alert('Account and Name are required');
      return;
    }
    if (!formData.program_ids || formData.program_ids.length === 0) {
      alert('At least one program is required');
      return;
    }

    setSaving(true);
    try {
      const endpoint = student?.id
        ? `/api/admin/students/${student.id}`
        : `/api/admin/students`;

      const payload = {
        ...formData,
        email: formData.email.trim() || null,
        current_stripes: parseInt(formData.current_stripes) || 0,
        date_of_birth: formData.date_of_birth || null,
        notes: formData.notes || null,
        portal_section_overrides: formData.portal_section_overrides || null,
      };

      const response = student?.id
        ? await adminApi.put(endpoint, payload)
        : await adminApi.post(endpoint, payload);

      if (response.ok) {
        const savedStudent = await response.json();
        onSave(savedStudent);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save student');
      }
    } catch (error) {
      console.error('Failed to save student:', error);
      alert('Failed to save student');
    } finally {
      setSaving(false);
    }
  };

  const toggleProgram = (programId) => {
    setFormData(prev => ({
      ...prev,
      program_ids: prev.program_ids.includes(programId)
        ? prev.program_ids.filter(id => id !== programId)
        : [...prev.program_ids, programId]
    }));
  };

  // Get selected customer and determine if it's an individual account
  const selectedCustomer = customers.find(c => c.id === formData.customer_id);
  const isIndividualAccount = selectedCustomer?.account_type === 'individual';

  // Get customer IDs that already have students (for individual accounts)
  const customerIdsWithStudents = new Set(
    students?.map(s => s.customer_id) || []
  );

  // Filter customers to only show valid accounts:
  // - Not cancelled (unless it's the current selection)
  // - For individual accounts: only show if they don't already have a student
  //   (unless we're editing that student's record)
  const validCustomers = customers.filter(c => {
    // Always include the currently selected customer (for editing)
    if (c.id === formData.customer_id) return true;

    // Exclude cancelled accounts
    if (c.subscription_status === 'cancelled') return false;

    // For individual accounts, exclude if they already have a student
    if (c.account_type === 'individual' && customerIdsWithStudents.has(c.id)) {
      return false;
    }

    return true;
  });

  // Handle customer selection - auto-fill name for individual accounts
  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer?.account_type === 'individual') {
      // For individual accounts, sync name with customer name
      setFormData(prev => ({
        ...prev,
        customer_id: customerId,
        name: customer.name || prev.name,
        email: '' // Individual accounts don't need separate student email
      }));
    } else {
      setFormData(prev => ({ ...prev, customer_id: customerId }));
    }
  };

  // Sync student name when editing individual account customer name
  useEffect(() => {
    if (isIndividualAccount && selectedCustomer && !student?.id) {
      setFormData(prev => ({
        ...prev,
        name: selectedCustomer.name || prev.name
      }));
    }
  }, [selectedCustomer?.name, isIndividualAccount, student?.id]);

  return (
    <Card className="border-2 border-blue-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">
          {student?.id ? 'Edit Student' : 'Add New Student'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Customer Selection */}
            <div>
              <Label htmlFor="customer_id">Account *</Label>
              <select
                id="customer_id"
                value={formData.customer_id}
                onChange={(e) => handleCustomerChange(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-background text-foreground"
                disabled={!!student?.id}
              >
                <option value="">Select Customer...</option>
                {validCustomers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.email} ({c.account_type === 'individual' ? 'Individual' : 'Parent'})
                  </option>
                ))}
              </select>
            </div>

            {/* Student Name */}
            <div>
              <Label htmlFor="name">Student Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full name"
                className="mt-1"
                disabled={isIndividualAccount}
              />
              {isIndividualAccount && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Name synced with customer account.
                </p>
              )}
            </div>

            {/* Student Email (for independent login) - only for parent accounts */}
            {!isIndividualAccount && (
              <div>
                <Label htmlFor="email">Student Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="student@example.com"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Optional. Allows student to sign in independently.
                </p>
              </div>
            )}

            {/* Date of Birth */}
            <div>
              <Label htmlFor="dob">Date of Birth</Label>
              <Input
                id="dob"
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
                className="mt-1"
              />
            </div>

            {/* Start Date */}
            <div>
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
                className="mt-1"
              />
            </div>

            {/* Current Belt */}
            <div>
              <Label htmlFor="belt">Current Belt</Label>
              <select
                id="belt"
                value={formData.current_belt}
                onChange={(e) => setFormData({ ...formData, current_belt: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-background text-foreground"
              >
                {beltOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Stripes */}
            <div>
              <Label htmlFor="stripes">Stripes (0-4)</Label>
              <Input
                id="stripes"
                type="number"
                min="0"
                max="4"
                value={formData.current_stripes}
                onChange={(e) => setFormData({ ...formData, current_stripes: e.target.value })}
                className="mt-1"
              />
            </div>

            {/* Belt Preview */}
            <div className="md:col-span-2 flex items-center gap-4 py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded">
              <span className="text-sm text-gray-600 dark:text-gray-400">Belt Preview:</span>
              <BeltBadge
                belt={formData.current_belt}
                stripes={parseInt(formData.current_stripes) || 0}
                size="md"
                showLabel
              />
            </div>

            {/* Programs */}
            <div className="md:col-span-2">
              <Label>Enrolled Programs</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {programs.map(program => (
                  <label
                    key={program.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transition-colors ${
                      formData.program_ids.includes(program.id)
                        ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-400 dark:border-blue-600'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.program_ids.includes(program.id)}
                      onChange={() => toggleProgram(program.id)}
                      className="rounded"
                    />
                    <span className="text-sm">{program.title}</span>
                  </label>
                ))}
                {programs.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No programs available</p>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes about the student..."
                className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-background text-foreground"
                rows={3}
              />
            </div>

            {/* Portal Section Overrides (only for existing students) */}
            {student?.id && (
              <div className="md:col-span-2">
                <Label className="mb-2 block">Portal Section Overrides</Label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Override which portal sections this student can see, beyond their program defaults.
                </p>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3 bg-gray-50/50 dark:bg-gray-900/30">
                  {SECTION_GROUPS.flatMap(group => {
                    const items = group.children.length > 0
                      ? group.children.map(c => ({ ...c, parent: group.label }))
                      : [{ id: group.id, label: group.label, parent: null }];
                    return items;
                  }).map(section => {
                    const overrides = formData.portal_section_overrides || {};
                    const currentValue = overrides[section.id] || 'default';
                    const isInProgram = effectiveSections?.effective_sections?.includes(section.id);
                    const programDefault = isInProgram ? 'visible' : 'hidden';

                    return (
                      <div key={section.id} className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                            {section.parent ? `${section.parent} > ` : ''}{section.label}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            programDefault === 'visible'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                          }`}>
                            {programDefault}
                          </span>
                        </div>
                        <select
                          value={currentValue}
                          onChange={(e) => {
                            const newOverrides = { ...overrides };
                            if (e.target.value === 'default') {
                              delete newOverrides[section.id];
                            } else {
                              newOverrides[section.id] = e.target.value;
                            }
                            setFormData({
                              ...formData,
                              portal_section_overrides: Object.keys(newOverrides).length > 0 ? newOverrides : null,
                            });
                          }}
                          className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-background text-foreground"
                        >
                          <option value="default">Default</option>
                          <option value="show">Show</option>
                          <option value="hide">Hide</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="flex space-x-2 mt-4">
            <Button type="submit" disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : (student?.id ? 'Update' : 'Add Student')}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// ============================================
// Student List Item
// ============================================
const StudentListItem = ({ student, customers, onDelete, onView, onViewPortal }) => {
  const customer = customers.find(c => c.id === student.customer_id);

  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => onView(student)}>
      <CardContent className="py-3 px-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div className="flex-1">
            <div className="flex items-center space-x-3">
              <span className="font-medium text-lg">{student.name}</span>
              <BeltIndicator belt={student.current_belt} stripes={student.current_stripes} />
              {student.email && (
                <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded text-xs">
                  Can sign in
                </span>
              )}
              {!student.is_active && (
                <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs">
                  Inactive
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
              <span className="flex items-center">
                <Users className="h-3 w-3 mr-1" />
                {customer?.name || customer?.email || 'Unknown'}
              </span>
              {student.start_date && (
                <span className="flex items-center">
                  <Calendar className="h-3 w-3 mr-1" />
                  Started: {new Date(student.start_date).toLocaleDateString()}
                </span>
              )}
              <span className="flex items-center">
                <Award className="h-3 w-3 mr-1" />
                {getBeltName(student.current_belt, student.current_stripes)}
              </span>
            </div>
            {student.notes && (
              <p className="text-xs text-gray-400 mt-1 truncate max-w-md">{student.notes}</p>
            )}
          </div>
          <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
            <Button variant="outline" size="sm" onClick={() => onViewPortal(student)} title="View member portal as this student">
              <ExternalLink className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => onDelete(student)}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================
// Student Detail View with Tabs
// ============================================
const STUDENT_DETAIL_TABS = ['overview', 'promotions', 'attendance', 'tournaments'];
const STUDENT_DETAIL_TAB_SCOPE = 'students.detail-tab';

const withTabParam = (searchParams, tab) => {
  const next = new URLSearchParams(searchParams);
  if (tab) next.set('tab', tab);
  else next.delete('tab');
  return next;
};

export const StudentDetail = ({ student, students, customers, programs, onBack, onEdit, onStudentUpdated, onViewPortal }) => {
  const [currentStudent, setCurrentStudent] = useState(student);
  const [detailSearchParams, setDetailSearchParams] = useSearchParams();
  const requestedDetailTab = detailSearchParams.get('tab');
  const hasExplicitTab = STUDENT_DETAIL_TABS.includes(requestedDetailTab);
  const activeTab = hasExplicitTab
    ? requestedDetailTab
    : getRememberedPageValue(
      STUDENT_DETAIL_TAB_SCOPE, STUDENT_DETAIL_TABS, 'overview', getAdminUser(),
    );
  const customer = customers.find(c => c.id === currentStudent.customer_id);
  const enrolledPrograms = programs.filter(p => currentStudent.program_ids?.includes(p.id));

  // Refresh student data when updated (e.g., after promotion/attendance changes)
  // Note: We only refresh the current student locally, not the parent's student list,
  // to avoid re-renders that would reset the active tab state
  const handleStudentUpdated = async () => {
    try {
      const response = await adminApi.get(`/api/admin/students/${currentStudent.id}`);
      if (response.ok) {
        const updated = await response.json();
        setCurrentStudent(updated);
      }
    } catch (error) {
      console.error('Failed to refresh student:', error);
    }
  };

  const setTabParam = (tab, options) => setDetailSearchParams(
    (current) => withTabParam(current, tab),
    options,
  );

  // Pin a queryless (or invalid) entry to the tab it is showing so Back and
  // Forward return to what was on screen; remember whatever is displayed.
  useEffect(() => {
    if (!hasExplicitTab) {
      setDetailSearchParams((current) => withTabParam(current, activeTab), { replace: true });
    }
  }, [activeTab, hasExplicitTab, setDetailSearchParams]);

  useEffect(() => {
    rememberPageValue(STUDENT_DETAIL_TAB_SCOPE, activeTab, STUDENT_DETAIL_TABS, getAdminUser());
  }, [activeTab]);

  const handleTabChange = (tab) => setTabParam(tab);

  // The tab parameter only describes the detail view; drop it from the list URL.
  const handleBack = () => {
    setTabParam(null, { replace: true });
    onBack();
  };

  return (
    <div className="space-y-4">
      <Button variant="outline" onClick={handleBack}>
        <ChevronLeft className="h-4 w-4 mr-2" />
        Back to Students
      </Button>

      {/* Student Header */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
            <div>
              <CardTitle className="text-2xl">{currentStudent.name}</CardTitle>
              <p className="text-gray-500 dark:text-gray-400">
                Parent: {customer?.name || customer?.email || 'Unknown'}
              </p>
            </div>
            <div className="flex flex-col gap-3 md:items-end">
              <div className="flex items-center gap-3">
                <BeltBadge
                  belt={currentStudent.current_belt}
                  stripes={currentStudent.current_stripes}
                  size="lg"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {getBeltName(currentStudent.current_belt, currentStudent.current_stripes)}
                </span>
              </div>
              <div className="flex gap-2 w-full md:w-auto">
                <Button
                  variant="outline"
                  className="flex-1 md:flex-none"
                  onClick={() => onViewPortal(currentStudent)}
                  title="View member portal as this student"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Portal
                </Button>
                <Button
                  className="flex-1 md:flex-none"
                  onClick={() => onEdit(currentStudent)}
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Tabbed Content */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid grid-cols-4 w-full max-w-lg h-auto">
          <TabsTrigger value="overview" className="flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1 py-1.5">
            <Users className="h-4 w-4" />
            <span className="text-[10px] sm:text-sm leading-tight">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="promotions" className="flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1 py-1.5">
            <Award className="h-4 w-4" />
            <span className="text-[10px] sm:text-sm leading-tight">Promotions</span>
          </TabsTrigger>
          <TabsTrigger value="attendance" className="flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1 py-1.5">
            <CheckCircle className="h-4 w-4" />
            <span className="text-[10px] sm:text-sm leading-tight">Attendance</span>
          </TabsTrigger>
          <TabsTrigger value="tournaments" className="flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1 py-1.5">
            <Trophy className="h-4 w-4" />
            <span className="text-[10px] sm:text-sm leading-tight">Tournaments</span>
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <Card>
            <CardContent className="pt-6 space-y-6">
              {/* Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-gray-500 dark:text-gray-400">Start Date</Label>
                  <p className="font-medium">
                    {currentStudent.start_date ? new Date(currentStudent.start_date).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500 dark:text-gray-400">Date of Birth</Label>
                  <p className="font-medium">
                    {currentStudent.date_of_birth ? new Date(currentStudent.date_of_birth).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500 dark:text-gray-400">Status</Label>
                  <p className="font-medium">
                    {currentStudent.is_active ? (
                      <span className="text-green-600">Active</span>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">Inactive</span>
                    )}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500 dark:text-gray-400">Current Belt</Label>
                  <p className="font-medium">{getBeltName(currentStudent.current_belt, currentStudent.current_stripes)}</p>
                </div>
              </div>

              {/* Student Email (Login) - Only show for parent accounts */}
              {customer?.account_type !== 'individual' && (
                <div>
                  <Label className="text-gray-500 dark:text-gray-400 mb-2 block">Student Login Email</Label>
                  {currentStudent.email ? (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-700 dark:text-gray-300">{currentStudent.email}</span>
                      <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded text-xs">
                        Can sign in independently
                      </span>
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Not set - Student cannot sign in independently</p>
                  )}
                </div>
              )}

              {/* Enrolled Programs */}
              <div>
                <Label className="text-gray-500 dark:text-gray-400 mb-2 block">Enrolled Programs</Label>
                {enrolledPrograms.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {enrolledPrograms.map(program => (
                      <span
                        key={program.id}
                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                      >
                        {program.title}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No programs enrolled</p>
                )}
              </div>

              {/* Notes */}
              {currentStudent.notes && (
                <div>
                  <Label className="text-gray-500 dark:text-gray-400 mb-2 block">Notes</Label>
                  <p className="text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-3 rounded">{currentStudent.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Promotions Tab */}
        <TabsContent value="promotions">
          <Card>
            <CardContent className="pt-6">
              <BeltPromotionEditor
                student={currentStudent}
                onStudentUpdated={handleStudentUpdated}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Attendance Tab */}
        <TabsContent value="attendance">
          <Card>
            <CardContent className="pt-6">
              <AttendanceManager
                student={currentStudent}
                students={students}
                programs={programs}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tournaments Tab */}
        <TabsContent value="tournaments">
          <Card>
            <CardContent className="pt-6">
              <TournamentEditor
                student={currentStudent}
                onStudentUpdated={handleStudentUpdated}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ============================================
// Main Student Management Component
// ============================================
const StudentManagement = () => {
  const [searchParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(
    () => searchParams.get('action') === 'add'
  );
  const [editingStudent, setEditingStudent] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showBulkAttendance, setShowBulkAttendance] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [studentsRes, customersRes, programsRes] = await Promise.all([
        adminApi.get('/api/admin/students'),
        adminApi.get('/api/admin/customers'),
        adminApi.get('/api/admin/programs'),
      ]);

      if (studentsRes.ok) {
        const data = await studentsRes.json();
        setStudents(data);
      }
      if (customersRes.ok) {
        const data = await customersRes.json();
        setCustomers(data);
      }
      if (programsRes.ok) {
        const data = await programsRes.json();
        setPrograms(data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (savedStudent) => {
    await fetchData();
    setShowEditor(false);
    setEditingStudent(null);
    if (selectedStudent?.id === savedStudent.id) {
      setSelectedStudent(savedStudent);
    }
  };

  const handleDelete = async (student) => {
    if (!window.confirm(`Are you sure you want to deactivate ${student.name}?`)) return;

    try {
      const response = await adminApi.delete(`/api/admin/students/${student.id}`);
      if (response.ok) {
        await fetchData();
      }
    } catch (error) {
      console.error('Failed to delete student:', error);
    }
  };

  const startEdit = (student) => {
    setEditingStudent(student);
    setShowEditor(true);
    setSelectedStudent(null);
  };

  const startAdd = () => {
    setEditingStudent(null);
    setShowEditor(true);
    setSelectedStudent(null);
  };

  // Handle viewing the member portal as a specific student
  const handleViewAsStudent = async (student) => {
    try {
      const response = await adminApi.post('/api/admin/impersonate/create-token', {
        student_id: student.id
      });

      if (response.ok) {
        const data = await response.json();
        // Open members portal in new tab with impersonation token
        window.open(data.redirect_url, '_blank');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create impersonation session');
      }
    } catch (error) {
      console.error('Failed to create impersonation token:', error);
      alert('Failed to start impersonation session');
    }
  };

  // Filter students
  const filteredStudents = students.filter(student => {
    const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesActive = filterActive ? student.is_active : true;
    return matchesSearch && matchesActive;
  });

  if (loading) {
    return <div className="text-center py-8">Loading students...</div>;
  }

  // Show student detail view
  if (selectedStudent) {
    return (
      <StudentDetail
        student={selectedStudent}
        students={students}
        customers={customers}
        programs={programs}
        onBack={() => setSelectedStudent(null)}
        onEdit={startEdit}
        onStudentUpdated={fetchData}
        onViewPortal={handleViewAsStudent}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Page Title */}
      {!showEditor && !showBulkAttendance && !selectedStudent && (
        <PageTitle
          title="Student Management"
          description="Manage students, belt progression, attendance, and tournaments"
          icon={<Users className="h-6 w-6" />}
          actions={
            <div className="flex gap-2">
              <Button onClick={startAdd}>
                <UserPlus className="h-4 w-4 mr-2" />
                Add Student
              </Button>
              <Button variant="outline" onClick={() => setShowBulkAttendance(true)}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Bulk Attendance
              </Button>
            </div>
          }
        />
      )}

      {/* Search and Filters */}
      {!showEditor && !showBulkAttendance && !selectedStudent && (
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 flex items-center gap-2">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <input
                type="checkbox"
                checked={filterActive}
                onChange={(e) => setFilterActive(e.target.checked)}
                className="rounded"
              />
              Active only
            </label>
          </div>

          <div className="text-sm text-gray-500 dark:text-gray-400">
            {filteredStudents.length} student{filteredStudents.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* Editor Form */}
      {showEditor && (
        <StudentEditor
          student={editingStudent}
          customers={customers}
          students={students}
          programs={programs}
          onSave={handleSave}
          onCancel={() => { setShowEditor(false); setEditingStudent(null); }}
        />
      )}

      {/* Bulk Attendance View */}
      {showBulkAttendance && (
        <div className="space-y-4">
          <Button variant="outline" onClick={() => setShowBulkAttendance(false)}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Students
          </Button>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Bulk Attendance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AttendanceManager
                students={students.filter(s => s.is_active)}
                programs={programs}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Students List */}
      {!showEditor && !showBulkAttendance && (
        <div className="space-y-2">
          {filteredStudents.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {students.length === 0
                ? 'No students yet. Click "Add Student" to create one.'
                : 'No students match your search.'}
            </div>
          ) : (
            filteredStudents.map(student => (
              <StudentListItem
                key={student.id}
                student={student}
                customers={customers}
                onDelete={handleDelete}
                onView={setSelectedStudent}
                onViewPortal={handleViewAsStudent}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default StudentManagement;
